<template>
  <div>
    <div class="filter-tabs">
      <ul class="nav nav-pills mb-2 tab-list">
        <li class="nav-item" v-for="filter in filters" :key="filter.id">
          <div
            class="nav-link"
            role="button"
            :class="{ active: filter.name == active_filter.name }"
            @click="selectFilter(filter)"
          >
            <span v-if="filter.name">{{ filter.name }}</span
            ><em v-else>{{ 'unsaved' | t }}</em>
          </div>
        </li>
      </ul>
    </div>
    <div class="search-area">
      <div class="fa fa-search"></div>
      <input class="search-field" :placeholder="$t('search')" v-model="temp_search_query" />
      <button class="btn btn-outline-primary d-none d-sm-inline" @click="$refs.add_filter_modal.open()">
        <div class="fa fa-sliders"></div>
        {{ 'add_filter' | t }}
      </button>
    </div>
    <div class="row" v-if="active_filter && active_filter.conditions.length > 0">
      <div class="col text-left pt-3">
        <div class="filter-wrapper" v-if="active_filter">
          <div class="badge badge-primary mr-3" v-for="(cond, i) in active_filter.conditions" :key="i">
            <span class="mr-2">
              {{ cond.attr | t }} {{ cond.op ? cond.op.label : 'equals' }}
              <span class="pl-1">{{ cond.value.label }}</span>
            </span>
            <a @click="remove_condition(i)"><i class="fa fa-times"></i> </a>
          </div>
          <a
            class="add-filter"
            @click="save_filter_as"
            v-if="active_filter.conditions.length > 0 && !active_filter.name"
          >
            {{ 'save_filter' | t }}
          </a>
          <a class="remove-filter" @click="delete_filter" v-else-if="active_filter.conditions.length > 0">
            {{ 'delete_filter' | t }}
          </a>
        </div>
      </div>
    </div>
    <div class="row" v-else-if="active_filter && !active_filter.built_in">
      <div class="col text-left pt-3">
        <div class="filter-wrapper">
          <a class="remove-filter" @click="delete_filter">{{ 'delete_filter' | t }}</a>
        </div>
      </div>
    </div>
    <sweet-modal ref="name_form" size="500">
      <div class="form-group text-left">
        <label>{{ 'name' | t }}</label>
        <input class="form-control" ref="new_name" />
      </div>
      <div class="form-group text-left">
        <button class="btn btn-primary" @click="$refs.name_form.onSave($refs.new_name.value)">{{ 'save' | t }}</button>
      </div>
    </sweet-modal>
    <sweet-modal ref="add_filter_modal" :title="title">
      <div class="row">
        <div class="col-10">
          <div class="row">
            <!-- Attribute-->
            <div class="col">
              <div class="form-group">
                <select class="form-control" v-model="new_condition.attr" @change="new_condition.value = null">
                  <option value="" disabled="disabled">{{ 'select_field' | t }}</option>
                  <option v-for="(attr, index) in attributes" :value="attr" :key="index">{{ attr.name | t }}</option>
                </select>
              </div>
            </div>
            <!-- Operator-->
            <div class="col">
              <div class="form-group">
                <select class="form-control" v-model="new_condition.op" :disabled="!new_condition.attr">
                  <option :value="op" v-for="(op, index) in valid_operators" :key="index">{{ op.label }}</option>
                </select>
              </div>
            </div>
            <!-- Value-->
            <div class="col">
              <div class="form-group">
                <!-- Dropdown-->
                <select class="form-control" v-model="new_condition.value" v-if="new_condition.attr.type == 'Array'">
                  <option :value="null" v-if="new_condition && new_condition.attr" disabled="disabled">
                    {{ 'select' | t }} {{ new_condition.attr.name | t | lowercase }}
                  </option>
                  <option :value="o" v-for="(o, index) in new_condition.attr.value" :key="index">{{ o.label }}</option>
                </select>
                <!-- Text input-->
                <input
                  class="form-control"
                  @input="new_condition.value = { val: $event.target.value, label: $event.target.value }"
                  v-else
                />
              </div>
            </div>
          </div>
          <!-- Actions-->
          <div class="row">
            <div class="col text-left">
              <button class="btn btn-sm btn-primary mt-2" @click="add_condition" :disabled="!new_condition.value">
                {{ 'add_filter' | t }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </sweet-modal>
  </div>
</template>

<script>
import _ from 'lodash';
import Qs from 'qs';
import Lockr from 'lockr';
import { mapState, mapGetters } from 'vuex';

export default {
  data: function () {
    return {
      group: '',
      title: this.$t('new_filter'),
      temp_search_query: '',
      search_query: '',
      built_in_filters: [], // Overridden
      show_add_filter: false,
      new_condition: {
        attr: '',
        op: '',
        value: null
      },
      operators: {
        Array: [
          {
            val: '=',
            label: this.$t('equal')
          },
          {
            val: '!=',
            label: this.$t('not_equal')
          }
        ],
        String: [
          {
            val: '=',
            label: this.$t('equal')
          },
          {
            val: '!=',
            label: this.$t('not_equal')
          },
          {
            val: 'LIKE',
            label: this.$t('contains'),
            formatter: (v) => '%' + v + '%'
          },
          {
            val: 'NOT LIKE',
            label: this.$t('not_contains'),
            formatter: (v) => '%' + v + '%'
          }
        ],
        StringEquality: [
          {
            val: '=',
            label: this.$t('equal')
          }
        ]
      },

      filters: [],

      active_filter: null,
      selected_attribute: null,
      show_filter_menu: false
    };
  },
  mounted() {
    this.before_mount();
    let filters = Lockr.get(this.ls_key);

    if (!filters || filters.length == 0) {
      filters = this.built_in_filters;
    }

    this.filters = filters;

    let name = Qs.parse(window.location.search, {
      ignoreQueryPrefix: true
    }).filter;
    let filter;
    if (typeof name === 'object') {
      filter = {
        name: null,
        conditions: [
          {
            attr: 'tag',
            op: { val: '=', label: 'equals' },
            value: { label: name.tag, val: name.tag }
          }
        ],
        built_in: false
      };
    } else {
      filter = _(this.filters).find((f) => f.name === name);
    }
    /**
     * Does a persisted filter exist, then try to "restore" that!
     */
    if (this.lastUsedFilter && this.lastUsedFilter.pathName === this.$route.name) {
      this.restorePersistedFilter();
    } else {
      if (filter) {
        this.active_filter = filter;
      } else {
        this.active_filter = this.filters[0];
      }
    }
    this.persistCurrentFilter();
    this.$emit('change', this.query);

    this.after_mount();
  },
  watch: {
    'active_filter.name': function () {
      if (this.active_filter && this.active_filter.name) {
        this.$router.replace({ query: { filter: this.active_filter.name } });
      }
    },
    query() {
      this.$emit('change', this.query);
    },
    show_filter_menu() {
      if (this.show_filter_menu) {
        this.new_condition.attr = null;
      }
    },
    temp_search_query() {
      this.update_search_query();
    },
    valid_operators() {
      if (this.valid_operators.length > 0) {
        this.new_condition.op = this.valid_operators[0];
      }
    }
  },
  computed: {
    ...mapGetters(['lastUsedFilter']),
    ls_key() {
      return 'filters_' + this.group;
    },
    query() {
      if (!this.active_filter) {
        return {};
      }

      let mapped = _.map(this.active_filter.conditions, (c) => [
        c.attr,
        {
          operator: c.op ? c.op.val : '=',
          value: c.op && c.op.formatter ? c.op.formatter.call(this, c.value.val) : c.value.val
        }
      ]);

      var filter = _.reduce(
        mapped,
        (mem, el) => {
          if (mem[el[0]]) {
            let new_value = el[1] && el[1]['value'] ? el[1]['value'] : el[1];
            mem[el[0]]['value'] = _.flatten([mem[el[0]]['value'], new_value]);
          } else {
            mem[el[0]] = el[1];
          }

          return mem;
        },
        {}
      );

      if (this.search_query.length > 0) {
        filter.free_text = this.search_query;
      }

      return {
        filter: filter
      };
    },
    valid_operators() {
      if (this.new_condition.attr) {
        return this.operators[this.new_condition.attr.type];
      } else {
        return [];
      }
    },
    attributes() {
      return []; // to be overridden
    }
  },
  methods: {
    before_mount() {},
    after_mount() {},
    update_search_query: _.debounce(function () {
      this.search_query = this.temp_search_query;
    }, 450),
    delete_filter() {
      this.filters = _(this.filters)
        .filter((f) => f.name != this.active_filter.name)
        .value();

      this.active_filter = this.filters[0];
      Lockr.set(this.ls_key, this.filters);
    },
    /**
     * Selects the filter that was clicked in filter-topbar
     */
    selectFilter(filter) {
      this.active_filter = filter;
      this.persistCurrentFilter();
    },
    save_filter() {
      if (this.active_filter.name === null) {
        this.save_filter_as();
        return;
      }

      Lockr.set(this.ls_key, this.filters);
    },
    /**
     * Persists active_filter so that it can be restored when navigating away and back.
     */
    persistCurrentFilter() {
      this.$store.commit('setLastSavedFilter', { filter: this.active_filter, pathName: this.$route.name });
    },
    /**
     * Restores a temporary filter when navigating back to list.
     */
    restorePersistedFilter() {
      const lastUsedFilter = this.lastUsedFilter;
      // First see if the filter's name exists in all filters
      if (!lastUsedFilter || (lastUsedFilter && lastUsedFilter.pathName !== this.$route.name)) {
        return;
      }
      const matchedFilter = this.filters.find((filter) => filter.name === lastUsedFilter.filter.name);
      if (matchedFilter) {
        // Then select it!
        this.active_filter = matchedFilter;
        // If it does, does it have a different length of conditions?
        if (this.active_filter.conditions.length !== lastUsedFilter.filter.conditions.length) {
          this.active_filter.conditions = { ...lastUsedFilter.filter.conditions };
        }
      } else {
        // We are dealing with an unsaved filter here :o
        this.active_filter = lastUsedFilter.filter;
        this.add_filter();
      }
    },
    save_filter_as() {
      this.$refs.name_form.open();

      this.$refs.name_form.onSave = (name) => {
        if (!name) {
          return;
        }
        if (this.active_filter.built_in) {
          this.add_filter();
        }

        this.active_filter.name = name;

        Lockr.set(this.ls_key, this.filters);

        this.$refs.name_form.close();
      };
    },
    add_filter() {
      let n = {
        name: null,
        conditions: _.cloneDeep(this.active_filter.conditions),
        built_in: false
      };
      this.filters.push(n);
      this.active_filter = n;
    },
    getAttr(attr) {
      return _.find(this.attributes, (a) => a.name == attr);
    },
    remove_condition(index) {
      if (this.active_filter.built_in) {
        this.add_filter();
      }
      this.active_filter.conditions.splice(index, 1);
    },
    add_condition() {
      if (!this.active_filter.conditions) {
        return;
      }
      if (!this.new_condition.attr.name || !this.new_condition.op || !this.new_condition.value) {
        return;
      }

      if (this.active_filter.built_in) {
        this.add_filter();
      }

      this.active_filter.conditions.push({
        attr: this.new_condition.attr.name,
        op: this.new_condition.op,
        value: this.new_condition.value
      });
      this.show_add_filter = false;
      this.new_condition.attr = '';
      this.new_condition.op = {};
      this.new_condition.value = {};
      this.show_filter_menu = false;
      this.persistCurrentFilter();
      this.$refs.add_filter_modal.close();
    }
  }
};
</script>

<style scoped>
.search-area {
  height: 55px;
  padding-bottom: 30px !important;
  vertical-align: middle;
  border-bottom: 1px solid #f0f3f4 !important;
}

.search-area .fa-search {
  position: absolute;
  margin-top: 8px;
  margin-left: 20px;
  font-size: 24px;
  font-weight: normal;
}

.add-filter {
  margin-right: 0;
}

.search-area .search-field {
  width: 80%;
  height: 43px;
  margin-left: 53px;
  font-size: 16px;
  line-height: 25px;
  border: 0;
}

input.search-field:focus {
  outline: none;
}

.filter-tabs > .nav-pills > .nav-item {
  padding: 5px;
}

.filter-tabs > .nav-pills > .nav-item > .nav-link {
  margin-bottom: -7px;
}

.filter-tabs > .nav-pills > .nav-item > .nav-link.active {
  color: #0b5268;
  background: none;
  border-bottom: 4px solid #0b5268;
  border-radius: 0;
}

.tab-list {
  width: 100%;
  border-bottom: 1px solid #f0f3f4 !important;
}

.filters-dropdown-menu {
  position: absolute;
  z-index: 5000;
  display: block;
  min-width: 400px;
  min-height: 180px;
  padding: 15px;
  margin-top: 120px;
  background-color: #fff;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, .2);
  border: 1px solid lightgray;
  border-radius: 5px;
}

.filters-dropdown {
  position: relative;
}

.filters-dropdown-menu input,
.filters-dropdown-menu select {
  width: 100% !important;
}

.filters-dropdown button {
  position: relative;
}

.order-search .fa-search {
  position: absolute;
  z-index: 9999 !important;
  margin-top: 10px;
  margin-left: 10px;
  color: rgba(0, 0, 0, .4);
  font-size: 12px;
}

.order-search {
  width: 100% !important;
}

.search-area .btn {
  position: absolute;
  right: 8px;
  margin-top: 5px;
}

.filter-wrapper {
  display: inline;
  padding: 5px;
  margin-top: 40px !important;
  margin-left: 20px;
  border: 1px dashed lightgray;
  border-radius: 5px;
}

.badge {
  padding: 2px 8px 2px 8px !important;
  font-size: 12px;
  font-style: normal;
  font-weight: normal;
  line-height: 16px;
  vertical-align: middle;
  border-radius: 20px;
}
</style>
